import {
  createElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  Outlet as _Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {useMutation, useQuery} from "@tanstack/react-query";

import _Toolbar from "./layout/Toolbar";
import "./App.css";
import "./css/fonts.css";
import {API} from "./api.js";
import {introspectScenePayload, formatBytes} from "./libs/sceneIntrospection";
import {serializeEnvelope} from "./libs/requestEnvelope";
import {getRenderReadyState, selectBlockingPage, selectLoadFailure} from "./libs/loadFailure";
import {
  buildExportCompletionAnalytics,
  buildExportFailure,
  buildExportFailureAnalytics,
} from "./libs/exportFailure";
import {checkSceneForInlineBlobs} from "./libs/sceneValidation";
import {openErrorDialog} from "./layout/ErrorDialog";
import {CONTENT_TYPES, layoutAmountFor} from "./data/products.js";
import {getHashes} from "./libs/bg-utils";
import {downloadServerExportBundle, renderServerScene, sendGAEvent} from "./libs/serverExport";
import {track} from "./libs/analytics";
import {renderServerExportBatches} from "./libs/serverExportScene";
import {materializeVoucherDataRow} from "./libs/voucherData";
import {SCENE_FIELDS as EDITS_SCENE_FIELDS, useEdits} from "./contexts/edits";
import {IMAGES_SCENE_FIELDS, readImageSceneField, useImages} from "./contexts/images";
import {SETTING_SCENE_FIELDS, prunePixelationForSave, useSetting} from "./contexts/setting";
import {drawnTemplatesFor} from "./data/templates";
import {templateMembersSnapshot} from "./data/templateSlots";
import {useTemplateSelection, useUnknownSceneTemplateMembers} from "./hooks/useTemplateSelection";
import {BLACK_TEXT_SET} from "./data/products/index";
import {palettes} from "data/palettes";
import {useScene} from "hooks/useScene";
import {useSceneBackgroundSelection} from "hooks/useSceneBackgroundSelection";
import {useChannelFromParams} from "hooks/useChannelFromParams";
import {useProductFromParams} from "hooks/useProductFromParams";
import {useContentTypeFromParams} from "hooks/useContentTypeFromParams";
import {useCopyUrl} from "hooks/useCopyUrl";
import {SceneNotFound as _SceneNotFound} from "pages/SceneNotFound/SceneNotFound";
import {WrongBrowser as _WrongBrowser} from "pages/WrongBrowser";
import {LoadError as _LoadError} from "pages/LoadError/LoadError";
import {usePointCloudBackground} from "hooks/usePointCloudBackground";
import {useInitialScene} from "hooks/useInitialScene";
import {useDefaultSceneOverrides} from "hooks/useDefaultSceneOverrides";
import {useCheckPermissions} from "hooks/useCheckPermissions";
import {useCheckBrowser} from "hooks/useCheckBrowser";
import {useBackgrounds} from "hooks/useBackgrounds";
import {shouldUseBgDerivation} from "hooks/useBgDerivation";
import {sessionBgScope, useSessionBgUploads, withSessionBgUploads} from "libs/sessionBgUploads";
import {useEffectiveBgTransform, useBackgroundCatalog} from "hooks/useEffectiveBgTransform";
import {composeSceneBgTransforms, buildBackgroundsOverride} from "libs/bgTransform";
import {BgTransformContext} from "contexts/bgTransform";
import {BAKED_BG_CHANNELS} from "libs/bakedBg";
import {getBgOps, registeredBgTemplateIds} from "libs/bgOps";
import {collectBakeJobs} from "layout/DefaultScenePanel/bakeOnSave";
import {useBgUrl} from "hooks/useBgUrl";
import {markerClassNames} from "libs/bgMarkers";
import {useBgCatalogFallbackIndex, usePublishBgCatalogIndex} from "hooks/useBgCatalogIndex";
import {shouldUseBgListEditing, useBgList} from "hooks/useBgList";
import {activePinnedBgUrl, buildSceneBgSource} from "libs/sceneBgSource";
import {linkBackgroundOf} from "layout/BgSelector/linkTile";
import {useEnsureDefaultEdits} from "hooks/useEnsureDefaultEdits";
import {useCurrentSet} from "hooks/useCurrentSet";
import {useSetBackgroundOrderOf} from "hooks/useSetBackgroundOrder";
import {hiddenBackgroundsOf, orderedBySet} from "libs/selectorVisibility";
import {numberedVariations} from "libs/bgNumbering";
import {pageGalleryTiles} from "layout/BgSelector/galleryTiles";
import {useChannelSettings} from "hooks/useChannelSettings";
import {useKeepUrlBackgroundNumber, usePublishBackgroundNumbers} from "hooks/useBackgroundNumbers";
import {useLogoCycling} from "hooks/useLogoCycling";
import {getAllProductsTemplateInstanceIds, getReleaseProductCodesForType} from "utils/releases";
import adsBgSources from "data/ads-bg-sources.json";
import {getGdnBackgroundBase, getGdnBgTilesets, getGdnCatalogBackgrounds} from "libs/gdn-bg";
import GdnNewsPreview from "pages/Ads/GdnNewsPreview";

// Reference-stable fallback — a fresh `[]` would re-fire every dep that sees it.
const EMPTY_BG_URLS = [];

/**
 * For the Ads channel: synthesize a Kraken-shaped allBgUrls entry pointing at
 * the full-size, uncropped, unrotated source photo we uploaded for this
 * (channel, product, contentType, bgVariation). Returns null if no source
 * is registered. The BgSelector consumes Kraken-shaped URLs that must
 *  (a) end in `.png` to pass `endsWith` and
 *  (b) contain `/<product>(?=[_.])` to pass findBgUrl's pattern.
 * Source URLs from the v3 baker (`/cai/baker/uploads/<id>.png`) end in `.png`
 * but don't contain the `/JB.` segment. We append `#/<product>.png` as a
 * hash fragment — fragments are client-side only, so the CDN ignores them
 * and serves the real PNG. After getThumbnailUrl swaps the trailing `.png`
 * with `.webp` (last-occurrence regex), the URL becomes
 * `…<id>.png#/JB.webp` which still resolves to the same PNG.
 */
function getAdsSourceTilesetUrls(channelValue, typeValue, productValue, bgVariation) {
  if (channelValue !== "ads") return null;
  const key = `ads/${productValue}/${typeValue}/${bgVariation}`;
  const entry = adsBgSources[key];
  if (!entry?.url) return null;
  return [[`${entry.url}#/${productValue}.png`]];
}

const App = ({role: authRole, featureFlags = {}}) => {
  const editsCtx = useEdits();
  const imagesCtx = useImages();
  const settingCtx = useSetting();
  const {titles, editProductNames, dataRows, reset: resetEdits} = editsCtx;
  const {
    bgX,
    bgY,
    bgScale,
    bgFlip,
    bgRotate,
    uploadImages,
    bakedBgs,
    logoHidden,
    editBgBrightness,
    editBgBlurValue,
    editBgX,
    editBgY,
    editBgScale,
    editBgRotate,
    editBgFlip,
    reset: resetImages,
  } = imagesCtx;
  const {
    bgOption,
    perProductBg,
    product,
    channel,
    type,
    // Every fetch and every write below keys on the product's home scope, not
    // on the channel the user came in through: JetBrains Air is one offering
    // that two channels list. See src/data/offering.ts.
    dataChannel,
    dataType,
    dataChannelObj,
    dataTypeObj,
    layoutOption,
    variantOption,
    addVisible,
    resetVisible,
    editLayoutAmount,
    editIsExportLoading,
    editProduct,
    editChannel,
    editType,
    base,
    editBase,
    hash,
    locked,
    reset: resetSetting,
  } = settingCtx;
  const navigate = useNavigate();
  const location = useLocation();
  const {contentType, sceneId} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRenderMode = searchParams.get("render") === "true";

  useEffect(() => {
    document.body.classList.toggle("render-mode", isRenderMode);
    return () => document.body.classList.remove("render-mode");
  }, [isRenderMode]);

  // URL → context sync. Previously lived in Toolbar (excluded in render mode),
  // which broke cross-channel SPA navigation for server-side rendering.
  const channelFromParams = useChannelFromParams();
  const productFromParams = useProductFromParams();
  const contentTypeFromParams = useContentTypeFromParams();

  // Only sync URL params → context when NOT on a /scene/:sceneId route.
  // Scene routes have no product/channel/type in the URL, so the hooks
  // return defaults — applying them would overwrite the scene-loaded state.
  useLayoutEffect(() => {
    if (sceneId) return;
    editChannel(channelFromParams);
  }, [editChannel, channelFromParams, sceneId]);

  useLayoutEffect(() => {
    if (sceneId) return;
    if (product?.value !== productFromParams?.value) {
      editProduct(productFromParams);
      // Reset editable product names so templates fall back to the new product.label
      editProductNames({});
    }
  }, [editProduct, editProductNames, product, productFromParams, sceneId]);

  useLayoutEffect(() => {
    if (sceneId) return;
    editType(contentTypeFromParams);
  }, [editType, contentTypeFromParams, sceneId]);

  useLayoutEffect(() => {
    if (!location.state?.resetOnArrival) {
      return;
    }

    resetEdits();
    resetImages();
    resetSetting();
    API.clearBackgroundCache();

    const nextState =
      location.state && typeof location.state === "object" ? {...location.state} : null;

    if (nextState) {
      delete nextState.resetOnArrival;
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState && Object.keys(nextState).length > 0 ? nextState : null,
    });
  }, [location, navigate, resetEdits, resetImages, resetSetting]);

  const {isWrongBrowserDetected} = useCheckBrowser({disabled: isRenderMode});
  useCheckPermissions({disabled: isRenderMode});

  // Fallback for old url like video/WS/fae40423
  useEffect(() => {
    if (contentType) {
      const foundType = CONTENT_TYPES.find((t) => t.value === contentType);
      if (!foundType) {
        window.location.href = `/scene/${contentType}`;
      }
    }
  }, [contentType]);

  // Moved up from further below so it's available to gate channel-dependent
  // fetches. useScene only reads sceneId + context dispatchers — safe here.
  const {
    isLoaded: isSceneLoaded,
    isError,
    error,
    refetch: retryScene,
    data: sceneData,
  } = useScene();

  // On /scene/:id, channel/product/type context starts at the UI's default
  // and is corrected only once scene data arrives. Firing default-scenes or
  // get_tilesets before then wastes a round trip on the wrong channel. Hold
  // both off until the scene loads; for non-scene routes this is a no-op.
  const isSceneRoute = Boolean(sceneId);
  const isChannelReady = !isSceneRoute || isSceneLoaded;

  // GDN displays the bundled v3-upload copy of Social's source artwork. Its
  // route identity stays ads/google-display-network: default-scene resolution
  // and transform precedence must never fall back to Social's legacy scenes.
  const isGoogleDisplayNetwork = channel.value === "ads" && type.value === "google-display-network";

  // Phase-2 background-transform derivation (issue #528, 2a). When the
  // bgDerivation flag is on, the displayed/saved bg crop is DERIVED by fixed
  // precedence from React Query (catalog + resolved scene) + user intent,
  // instead of being written into the images context by whichever async fetch
  // resolves last (the old three-writer race). Inert when the flag is off: the
  // selector's queries don't fire and getEffectiveTransform returns null, so
  // BgTransformer/buildScene keep their legacy context-driven behavior.
  // Scoped to non-saved routes: on /scene/:id the crop is loaded into the
  // images context by useScene (LOAD_SCENE), which the derivation path does not
  // read. Saved scenes keep their persisted crop until an explicit background
  // selection applies its defaults through useSceneBackgroundSelection below.
  const bgDerivation = shouldUseBgDerivation({
    featureEnabled: featureFlags.bgDerivation === true,
    sceneId,
  });

  // Which design of each slot this page draws: the saved scene's, or the live
  // choice of an admin (data/templateSlots.ts).
  const templateSelection = useTemplateSelection();
  const unknownSceneTemplateMembers = useUnknownSceneTemplateMembers();

  // Editable background list (plan D1, admin only). When on, the sidebar
  // gallery is the admin's own draft arrangement — reordered, with deletions
  // and uploads — instead of the raw catalog, and every positional consumer
  // downstream reads it through the same publish points the catalog uses.
  // Every other role stays on /api/v3/backgrounds, which already returns the
  // published order and the same positions.
  const bgListEditing = shouldUseBgListEditing({
    featureFlags,
    sceneId,
    channel: dataChannel,
    role: authRole,
  });

  // Server-filtered slice: the editor only resolves overrides for the current
  // product/type, so don't pull the whole channel — the collection is
  // unbounded and grows with every release rollout.
  //
  // Under bgDerivation the page path doesn't need this list at all (issue #567,
  // A4): useInitialScene resolves its single key against
  // GET /api/v3/default-scenes/resolved instead of cascading over every row.
  // The admin panel still lists rows via v2, but it fetches its own slice while
  // open (DefaultScenePanel), so nothing on the read path depends on this.
  const overridesState = useDefaultSceneOverrides(dataChannelObj, {
    enabled: isChannelReady && !bgDerivation,
    product: product?.value,
    type: dataType,
  });
  const currentSet = useCurrentSet();
  const {
    defaultBase,
    isInitialSceneLoaded,
    isDefaultSceneSettled,
    isInitialSceneReady,
    sceneHasBgTransforms,
    hasInitialSceneTransforms,
    hasRouteDefaultScene,
    initialSceneBgOption,
    isOverrideDriven,
    isError: isInitialSceneError,
    error: initialSceneError,
    retry: retryInitialScene,
  } = useInitialScene(dataChannelObj, product, dataTypeObj, currentSet, overridesState);
  const pointCloudError = usePointCloudBackground(
    isSceneRoute ? isSceneLoaded : isInitialSceneReady,
    bgDerivation
  );

  // GET /api/v3/bg-list. Idle unless the gate holds, so a flag-off route is
  // byte-identical to before.
  const bgListQuery = useBgList({
    channel: dataChannel,
    type: dataType,
    product: product.value,
    enabled: bgListEditing,
  });
  const bgListBackgrounds = bgListEditing ? (bgListQuery.data?.entries ?? null) : null;
  // Temporary tiles of images uploaded in this page (libs/sessionBgUploads.ts).
  const sessionBgUploads = useSessionBgUploads(
    sessionBgScope(dataChannel, dataType, product.value)
  );

  const savedSceneGallery =
    featureFlags.bgDerivation === true && isSceneRoute && isSceneLoaded &&
    dataChannel !== "ads" && dataChannel !== "livestream" && product.value !== "ALL";
  const previewsEnabled = bgDerivation || (savedSceneGallery &&
    (!isRenderMode || searchParams.has("bg") || searchParams.has("background")));
  const effectiveBgTransforms = useEffectiveBgTransform({
    enabled: bgDerivation,
    previewsEnabled,
    // Under the editable list the resolved entries also hold the admin's
    // draft uploads, which the catalog does not have.
    catalogBackgrounds: bgListBackgrounds,
    extraBackgrounds: sessionBgUploads,
    channel: dataChannel,
    type: dataType,
    product: product.value,
    set: currentSet,
    layout: layoutOption,
    background: bgOption,
    // The brand treatment is the fourth key axis of a default-scene row, so
    // the derived crop must resolve under the same cell the editor shows.
    variant: variantOption,
    perProductBg,
    // Which templates are detached: a locked template renders its own
    // per-template upload instead of the gallery image, so the catalog's crop
    // stops describing it (templateShowsCatalogImage).
    locked,
  });
  const {
    getEffectiveTransform,
    transformsReady: derivedTransformsReady,
    getTileImage,
    getTileTransform,
    getTileSavedPreviewTemplate,
    getTileLayoutImages,
    getTileBase,
  } = effectiveBgTransforms;
  const sceneBackgroundSelection = useSceneBackgroundSelection({
    enabled: savedSceneGallery,
    sceneId,
    getTileDefaults: effectiveBgTransforms.getTileDefaults,
    getTileTransform,
    tileError: effectiveBgTransforms.tileError,
  });
  const transformsReady = derivedTransformsReady && !sceneBackgroundSelection.isPending &&
    !sceneBackgroundSelection.isError;
  // Baked backgrounds (libs/bakedBg.ts): a banner on a baking channel shows
  // nothing until the route's default scene is known — applied, absent, or
  // failed. A saved scene carries its own backgrounds and does not wait.
  const awaitingDefaultScene =
    (BAKED_BG_CHANNELS.has(dataChannel) && !sceneId && !isDefaultSceneSettled) ||
    sceneBackgroundSelection.isPending;
  const bgTransformContextValue = useMemo(
    () => ({
      enabled: bgDerivation,
      previewsEnabled,
      getEffectiveTransform,
      transformsReady,
      getTileImage,
      getTileTransform,
      getTileSavedPreviewTemplate,
      getTileLayoutImages,
      getTileBase,
      awaitingDefaultScene,
    }),
    [
      bgDerivation,
      previewsEnabled,
      getEffectiveTransform,
      transformsReady,
      getTileImage,
      getTileTransform,
      getTileSavedPreviewTemplate,
      getTileLayoutImages,
      getTileBase,
      awaitingDefaultScene,
    ]
  );

  // Ads backgrounds are local canonical assets and must not depend on Kraken.
  // bgOption can be "generative" or "custom" — non-numeric selection states.
  // The sidebar source list is independent of selection, so coerce to a
  // numeric variation for the lookup; otherwise the key misses and the
  // default ads-source tile disappears when generative/custom is active.
  // Returns null for google-display-network (no GDN entries in the ads
  // manifest), so GDN keeps the real fetch path — see gdnSourceUrls below.
  const adsSourceUrls = useMemo(
    () =>
      getAdsSourceTilesetUrls(
        channel.value,
        type.value,
        product.value,
        Number.isFinite(Number(bgOption)) ? String(bgOption) : "1"
      ),
    [channel.value, type.value, product.value, bgOption]
  );

  // Phase-3 (issue #562, M3): under bgDerivation, source the background LIST from
  // the v3 catalog and inject it via backgroundsOverride — the same mechanism the
  // Ads/GDN channel already uses. This makes useBackgrounds skip Kraken
  // get_tilesets entirely (shouldFetchRemoteBackgrounds is false when an override
  // is present). Empty ([]) while the catalog loads — every tile channel is now
  // migrated, so there's no legacy path to fall back to. Ads keeps its own
  // override; when the flag is off, this is null and the legacy get_tilesets runs.
  // The saved-scene route stays on the legacy tile path, but it needs the
  // catalog too: a catalog-only background (JetBrains Air's artwork, a promoted
  // upload) has no tile, and without the catalog every saved scene — and the
  // server export, which renders /scene/:id — lost it (issue #698). Fetched
  // only once the scene has loaded, so the request names the scene's own
  // channel and product, not the UI defaults the route starts on.
  const catalogForSavedScene = isSceneRoute && isSceneLoaded;
  const catalogQuery = useBackgroundCatalog({
    channel: dataChannel,
    type: dataType,
    product: product.value,
    enabled: bgDerivation || catalogForSavedScene,
  });
  // One list feeds every positional consumer below — the sidebar tile count,
  // the display-image index and the bg-linked base — so they cannot disagree
  // about which image stands at a position. Under the editable list that is the
  // resolved arrangement; otherwise the raw catalog, exactly as before.
  const baseDisplayBackgrounds = bgListBackgrounds ?? catalogQuery.data?.backgrounds;
  const displayBackgrounds = useMemo(
    () => withSessionBgUploads(baseDisplayBackgrounds, sessionBgUploads),
    [baseDisplayBackgrounds, sessionBgUploads]
  );
  // Whichever query owns that list also owns the pending/error status the page
  // holds on, and the retry the failure dialog offers.
  const displayBackgroundsQuery = bgListEditing ? bgListQuery : catalogQuery;
  const catalogOverride = useMemo(
    () => (bgDerivation ? (buildBackgroundsOverride(displayBackgrounds) ?? []) : null),
    [bgDerivation, displayBackgrounds]
  );
  const gdnSourceUrls = isGoogleDisplayNetwork ? getGdnBgTilesets() : null;
  const backgroundsOverride =
    gdnSourceUrls ?? (adsSourceUrls !== null ? adsSourceUrls : catalogOverride);
  // The catalog override is empty in two very different situations — still
  // loading, and failed — and an empty list renders as "this channel has no
  // backgrounds" (readiness even flips to image-ready). So the override carries
  // its query status: pending holds the page, an error reaches selectLoadFailure
  // below with a working retry. Ads is a bundled manifest: synchronous, no
  // status. Flag-off there is no override and the remote query owns the status.
  // The channel/type guard mirrors the catalog query's own `enabled`: a disabled
  // query reports isPending forever, which would be a hang rather than a wait.
  const backgroundsOverrideStatus = useMemo(
    () =>
      catalogOverride !== null &&
      adsSourceUrls === null &&
      gdnSourceUrls === null &&
      Boolean(dataChannel && dataType)
        ? {
            isPending: displayBackgroundsQuery.isPending,
            isSuccess: displayBackgroundsQuery.isSuccess,
            isError: displayBackgroundsQuery.isError,
            error: displayBackgroundsQuery.error,
            retry: displayBackgroundsQuery.refetch,
          }
        : null,
    [
      catalogOverride,
      adsSourceUrls,
      gdnSourceUrls,
      dataChannel,
      dataType,
      displayBackgroundsQuery.isPending,
      displayBackgroundsQuery.isSuccess,
      displayBackgroundsQuery.isError,
      displayBackgroundsQuery.error,
      displayBackgroundsQuery.refetch,
    ]
  );

  // Phase-B (issue #567, B3): publish the structured display-image index so
  // useBgUrl, BgSelector and the marker classNames below read urls/thumbnails/
  // markers from the catalog instead of matching substrings in Kraken filenames.
  // The flattened `catalogOverride` above stays: it's the legacy-shaped list the
  // flag-off path and the tile *count* still run on. Ads keeps its own synthetic
  // urls (they encode `#/<product>.png` precisely to satisfy findBgUrl), so no
  // index is published for it and its consumers stay on the legacy path.
  const gdnCatalogBackgrounds = useMemo(
    () => (isGoogleDisplayNetwork ? getGdnCatalogBackgrounds(product.value) : null),
    [isGoogleDisplayNetwork, product.value]
  );
  // The tile order. Each item's `variation` is its identity, so the order
  // travels separately from the variation-indexed url array. It comes from the
  // same list as the catalog index: on GDN that is the bundled GDN catalog,
  // not the (empty) channel catalog.
  const bgOrderSource = gdnCatalogBackgrounds ?? displayBackgrounds;
  // A releases set with its own tile order puts it first
  // (hooks/useSetBackgroundOrder.ts).
  const {order: setBgOrder} = useSetBackgroundOrderOf();
  const bgDisplayOrder = useMemo(
    () =>
      bgDerivation && bgOrderSource
        ? orderedBySet(
            bgOrderSource.map((b) => b.variation),
            (variation) => variation,
            setBgOrder
          )
        : null,
    [bgDerivation, bgOrderSource, setBgOrder]
  );
  const bgCatalogIndex = usePublishBgCatalogIndex({
    channel: dataChannel,
    type: dataType,
    product: product.value,
    // On /scene/:id the index is the raw catalog: a saved scene does not
    // carry the admin's draft uploads.
    backgrounds:
      gdnCatalogBackgrounds ??
      (catalogForSavedScene ? catalogQuery.data?.backgrounds : displayBackgrounds),
    // On the saved-scene route the index is a fallback, read through
    // useBgCatalogFallbackIndex after the tiles miss; the authoritative read
    // stays gated on bgDerivation inside useBgCatalogIndex.
    enabled: (bgDerivation || catalogForSavedScene) && adsSourceUrls === null,
  });
  // The index as readers see it: published to the query cache one commit
  // after it is built.
  const publishedCatalogIndex = useBgCatalogFallbackIndex(dataChannel, dataType);
  // A saved link offers the gallery its product page offers: the published
  // list of the scene's product. The legacy tiles still hold backgrounds an
  // admin deleted from it (2026-10-16). Ads, Livestream and the ALL view keep
  // their own sources, and a failed catalog read keeps the legacy tiles.
  const linkGalleryApplies =
    catalogForSavedScene &&
    featureFlags.bgDerivation === true &&
    adsSourceUrls === null &&
    !isGoogleDisplayNetwork &&
    dataChannel !== "livestream" &&
    product.value !== "ALL" &&
    Boolean(dataChannel && dataType);
  const linkGallery = linkGalleryApplies ? (catalogQuery.data?.backgrounds ?? null) : null;
  const isLinkGalleryPending = linkGalleryApplies && catalogQuery.isPending;
  // The background the link opened with: the selector gives it a tile of its
  // own when the gallery does not show it (layout/BgSelector/linkTile.ts).
  const linkBackground = useMemo(
    () => (linkGalleryApplies && isSceneLoaded ? linkBackgroundOf(sceneId, sceneData) : null),
    [linkGalleryApplies, isSceneLoaded, sceneId, sceneData]
  );
  // On /scene/:id the catalog is a fallback for backgrounds with no tile (Air)
  // and the source of the pinned image's markers. Until it is in, such a
  // background reads as "missing", and the server renderer captured that blank
  // state as final. Mirrors the catalog query's own `enabled`, so a disabled
  // query cannot hold the page forever.
  const savedSceneCatalogPending =
    catalogForSavedScene &&
    Boolean(dataChannel && dataType) &&
    (catalogQuery.isPending ||
      (Boolean(catalogQuery.data?.backgrounds?.length) && publishedCatalogIndex === null));

  // Load background data at app level so it's available in render mode
  // (previously only called inside Toolbar, which is excluded in render mode)
  const backgroundsData = useBackgrounds(dataChannel, dataType, product, {
    sceneHasBgTransforms,
    isInitialSceneLoaded,
    // Empty under bgDerivation (the list isn't fetched — see A4 above). It only
    // feeds this hook's flag-off transform-apply effects, which are inert then.
    overrides: overridesState?.overrides,
    set: currentSet,
    hasRouteDefaultScene,
    hasInitialSceneTransforms,
    initialSceneBgOption,
    hasOverrides: isOverrideDriven,
    enabled: isChannelReady,
    bgDerivation,
    sceneBackgroundSelection: sceneBackgroundSelection.active,
    backgroundsOverride,
    backgroundsOverrideStatus,
    linkGallery,
  });
  // The Ads channel sources its sidebar thumbnails from
  // `src/data/ads-bg-sources.json` (full-size, uncropped, unrotated photos
  // pulled from the original Figma imageRefs and uploaded to the v3 baker)
  // rather than from Kraken's `tiles/get_tilesets`. AI and DevEco use
  // *different* source photos (purple-light vs orange-light laptop), and
  // Kraken only has the AI tile, so this manifest is the canonical source
  // for both content types. The actual baked per-template bg is unchanged
  // — it still comes from `src/data/ads-assets.json`. See
  // `getAdsSourceTilesetUrls` above for the URL shape and rationale.
  const loadFailure = selectLoadFailure({
    scene: {isError, error, retry: retryScene},
    sceneTemplates: {
      isError: unknownSceneTemplateMembers.length > 0,
      error: new Error(`Unknown template designs: ${unknownSceneTemplateMembers.join(", ")}`),
      retry: () => window.location.reload(),
    },
    defaultScenes: {
      isError: overridesState.isError,
      error: overridesState.error,
      retry: overridesState.retry,
    },
    initialScene: {
      isError: isInitialSceneError || sceneBackgroundSelection.isError,
      error: sceneBackgroundSelection.isError ? sceneBackgroundSelection.error : initialSceneError,
      retry: sceneBackgroundSelection.isError ? effectiveBgTransforms.retryTiles : retryInitialScene,
    },
    backgrounds: {
      isError: backgroundsData.isError,
      error: backgroundsData.error,
      retry: backgroundsData.retry,
    },
  });
  const allBgUrls = backgroundsData.backgrounds ?? EMPTY_BG_URLS;
  useEnsureDefaultEdits();

  // The numbered tiles of the page (libs/bgNumbering.ts): the tiles that the
  // background selector draws, without the hidden ones. The URL names a
  // background by its number among them. Livestream has artworks, no
  // numbered tile. A page whose settings did not load shows every tile, as
  // the selector does. A number waits for these numbers, so each page must
  // publish them.
  const {data: pageChannelSettings, isError: pageChannelSettingsFailed} =
    useChannelSettings(dataChannel);
  const numberedBackgrounds = useMemo(() => {
    if (dataChannel === "livestream") return [];
    if (!pageChannelSettings && !pageChannelSettingsFailed) return null;
    if (backgroundsData.isPending || isLinkGalleryPending) return null;
    const tiles = pageGalleryTiles({
      catalogIndex: bgDerivation ? bgCatalogIndex : null,
      displayOrder: bgDisplayOrder,
      linkGallery,
      allBgUrls: backgroundsData.backgrounds,
      product: product.value,
      isAllProducts: product.value === "ALL",
      isGoogleDisplayNetwork,
    });
    return numberedVariations(
      tiles.map((tile) => tile.variation),
      hiddenBackgroundsOf(pageChannelSettings, dataType, product.value, currentSet)
    );
  }, [
    dataChannel,
    dataType,
    pageChannelSettings,
    pageChannelSettingsFailed,
    backgroundsData.isPending,
    backgroundsData.backgrounds,
    isLinkGalleryPending,
    bgDerivation,
    bgCatalogIndex,
    bgDisplayOrder,
    linkGallery,
    product.value,
    isGoogleDisplayNetwork,
    currentSet,
  ]);
  usePublishBackgroundNumbers(
    {sceneId, channel: dataChannel, type: dataType, product: product.value, set: currentSet},
    numberedBackgrounds
  );
  useKeepUrlBackgroundNumber();

  // Derive the current bg URL (+ outdated fallback, custom-upload, missing
  // states) via the shared hook. Templates consume the same hook — no
  // useImages().bgUrl relay needed.
  const {url: bgUrl, markers: bgMarkers} = useBgUrl();

  const [classNames, setClassNames] = useState("");
  const isFirstMount = useRef(true);

  // Class-name composition: bg style-marker classes (blacklogo, whitebg, grid…) +
  // BLACK_TEXT_SET override + product/channel/bg-option/layout classes.
  //
  // The markers come from useBgUrl, which knows whether the catalog or the
  // filename is the source for the background it resolved (#567, B3): the marker
  // list is a property of the image, not of its URL, and under bgDerivation the
  // URL is a content hash that carries nothing.
  useEffect(() => {
    const markerClasses = markerClassNames(bgMarkers);
    let cls = markerClasses ? ` ${markerClasses}` : "";
    if (BLACK_TEXT_SET.has(product.value) && !cls.includes("blacktext")) {
      cls += " blacktext";
    }
    cls += ` ${product.value} ${dataChannel}`;
    cls += bgOption !== "custom" ? ` bg-${bgOption}` : " bg-upload";
    if (layoutOption) cls += ` layout-${layoutOption}`;
    setClassNames(cls);
  }, [bgMarkers, product.value, dataChannel, bgOption, layoutOption]);

  // Deterministic "page is ready for screenshot" flag, polled by Playwright-
  // driven flows (server renderer + VR). One effect, recomputed on every
  // pathname change — splitting into reset+set used to leave the flag
  // stuck false for non-scene → non-scene SPA navigation (e.g. Toolbar's
  // channel switch from /releases/IJ/eap to /email/JB/general): the reset
  // fired on path change, but the setter's deps were [sceneId, isSceneLoaded]
  // and neither changed, so it never re-ran. Path is now in the deps so
  // the setter fires on every nav.
  useEffect(() => {
    const {ready, error: renderLoadError} = getRenderReadyState({
      sceneId,
      isSceneLoaded,
      hasRouteDefaultScene,
      isInitialSceneLoaded,
      loadFailure,
    });
    window.__renderReady = ready;
    if (renderLoadError) window.__renderLoadError = renderLoadError;
    else delete window.__renderLoadError;
    const trace = (window.__readinessTrace = window.__readinessTrace || []);
    trace.push({
      t: Math.round(performance.now()),
      phase: "renderReady",
      ready,
      path: location.pathname,
    });
  }, [
    location.pathname,
    sceneId,
    isSceneLoaded,
    hasRouteDefaultScene,
    isInitialSceneLoaded,
    loadFailure,
  ]);

  // ---------------------------------------------------------------------------
  // State reset for warm-page pool. The /render/batch endpoint calls
  // page.evaluate(() => window.__resetAppState()) before SPA navigation
  // to clear all stale state from the previous export.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    window.__resetAppState = () => {
      window.__renderReady = false;
      delete window.__renderLoadError;
      delete window.__SCENE_DATA__;
      document.body.classList.remove("image-ready");
      resetEdits();
      resetImages();
      resetSetting();
    };
    return () => {
      delete window.__resetAppState;
    };
  }, [resetEdits, resetImages, resetSetting]);

  // Expose bg transform setters for the smart-crop renderer endpoint.
  // Playwright calls page.evaluate(() => window.__setBgTransform(id, params))
  // to reposition backgrounds without a full page reload.
  useEffect(() => {
    if (!isRenderMode) return;
    window.__setBgTransform = (templateId, {x, y, scale, rotate, flip}) => {
      editBgX(templateId, x);
      editBgY(templateId, y);
      editBgScale(templateId, scale);
      if (rotate != null) editBgRotate(templateId, rotate);
      if (flip != null) editBgFlip(templateId, flip);
    };
    return () => {
      delete window.__setBgTransform;
    };
  }, [isRenderMode, editBgX, editBgY, editBgScale, editBgRotate, editBgFlip]);

  // Read-only state snapshot for Playwright assertions and debugging.
  // Usage: page.evaluate(() => window.__getState())
  useEffect(() => {
    window.__getState = () => ({
      bgX,
      bgY,
      bgScale,
      bgRotate,
      bgFlip,
      bgOption,
      bgUrl,
      // Raw URL/blob preview the upload pipeline writes; bgUrl derives from it.
      customBg: imagesCtx.customBg,
      // Each banner's own image for the background (templateBg.ts).
      customBgs: imagesCtx.customBgs,
      base,
      hash,
      layoutOption,
      // Scene-scoped settings the scope-switch reset must return to their
      // defaults — the fields the defaults-vs-inherited specs compare against
      // a fresh page load of the same URL.
      bgBrightness: imagesCtx.bgBrightness,
      bgBlurValue: imagesCtx.bgBlurValue,
      bgOpacity: imagesCtx.bgOpacity,
      variantOption: settingCtx.variantOption,
      templateModes: settingCtx.templateModes,
      layouts: settingCtx.layouts,
      variants: settingCtx.variants,
      selectedTemplates: settingCtx.selectedTemplates,
      // The page's banners (libs/bannerComposition.ts) and the eyes and order
      // over them.
      visible: settingCtx.visible,
      order: settingCtx.order,
      hiddenTemplates: settingCtx.hiddenTemplates,
      extraTemplates: settingCtx.extraTemplates,
      bgOpacities: imagesCtx.bgOpacities,
      isUppercase: settingCtx.isUppercase,
      fontOption: settingCtx.fontOption,
      generativeSpeed: settingCtx.generativeSpeed,
      pixelationEnabled: settingCtx.pixelationEnabled,
    });
    return () => {
      delete window.__getState;
    };
  });

  useLogoCycling();

  // Compute scene hashes linked to backgrounds (hashes are stored in tileset metadata URLs)
  const bgHashes = useMemo(() => {
    if (!Array.isArray(allBgUrls) || allBgUrls.length === 0) return {};
    return getHashes(allBgUrls, product.value);
  }, [allBgUrls, product.value]);

  const bgLinkedSceneId = useMemo(() => {
    if (bgOption === "custom") return null;
    const idx = Number(bgOption) - 1;
    if (!Number.isFinite(idx) || idx < 0) return null;

    const productHashes = bgHashes?.[product.value] || [];
    const defaultHashes = bgHashes?.default || [];
    return productHashes[idx] || defaultHashes[idx] || null;
  }, [bgOption, bgHashes, product.value]);

  const gdnBackgroundBase = useMemo(
    () => (isGoogleDisplayNetwork ? getGdnBackgroundBase(product.value, bgOption) : null),
    [isGoogleDisplayNetwork, product.value, bgOption]
  );

  // Load base color from the sceneId embedded in background tileset metadata (e.g. "..._&<sceneId>.png")
  const {data: bgLinkedSceneData} = useQuery({
    queryKey: ["fetch-bg-linked-scene", bgLinkedSceneId],
    queryFn: () => API.fetchScene(bgLinkedSceneId),
    // GDN reuses Social uploads through its bundled manifest. Those rows are
    // not in the v3 background catalog, so their linked scenes remain the only
    // source for the Social base color while derivation is enabled.
    enabled:
      Boolean(bgLinkedSceneId) &&
      !sceneId &&
      bgOption !== "custom" &&
      (!bgDerivation || (isGoogleDisplayNetwork && gdnBackgroundBase === null)),
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Under bgDerivation the same value comes from the catalog item (issue #567,
  // A3) — the migration captured each source scene's `base`, so there's nothing
  // to fetch. This also FIXES the flag-on path: the catalog's display URLs are
  // plain `<prod>.png`, carrying no `_&<hash>`, so getHashes above finds nothing
  // and the bg-linked base was silently dropped.
  const bgLinkedBase = useMemo(() => {
    if (isGoogleDisplayNetwork && gdnBackgroundBase !== null) return gdnBackgroundBase;
    if (!bgDerivation) return bgLinkedSceneData?.base ?? null;
    if (isGoogleDisplayNetwork) return bgLinkedSceneData?.base ?? null;
    if (bgOption === "custom") return null;
    const item = displayBackgrounds?.find((b) => String(b.variation) === String(bgOption));
    return item?.base ?? null;
  }, [
    bgDerivation,
    isGoogleDisplayNetwork,
    gdnBackgroundBase,
    bgLinkedSceneData,
    bgOption,
    displayBackgrounds,
  ]);

  // Reset base when the underlying context provides a fresh default
  // (channel / product / type switch loads a different initial scene,
  // defaultBase recomputes), then the second effect below overrides
  // once a bg-linked scene loads. Skip when the initial scene
  // (override / legacy) already provides its own base — otherwise the
  // bg-linked scene base ("000") would overwrite the saved default.
  //
  // bgOption is NOT in the deps, by design: re-running on bg changes
  // was the cause of the "base saves as 000" race for products without
  // an initial scene (Koog KG and others). User edits base → green,
  // clicks a different bg → effect re-fired and reset base back to
  // "000". The bgOption guard inside the effect still applies on the
  // legitimate fires (mount, sceneId/defaultBase change) so we don't
  // reset in generative / custom modes the user already opted into.
  const initialSceneHasBase = isInitialSceneLoaded && defaultBase !== "000";

  useEffect(() => {
    if (sceneId) return;
    if (bgOption === "custom" || bgOption === "generative") return;
    editBase(defaultBase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneId, defaultBase, editBase]);

  useEffect(() => {
    if (sceneId) return;
    if (bgOption === "custom") return;
    // Wait until the route default has finished applying. Otherwise a late
    // default with the universal "000" base can overwrite this background's
    // bundled base without changing any of the dependencies below.
    if (!isInitialSceneLoaded) return;
    // `=== null` (not falsy): the legacy scene payload could carry base "" and
    // this effect applied it, so keep that byte-identical with the flag off.
    if (bgLinkedBase === null) return;
    if (initialSceneHasBase) return;

    // Don't apply "000" — it's the universal default and would overwrite
    // user edits for products without an initial scene (e.g. KG). (The catalog
    // already stores null for it; the legacy scene payload does not.)
    if (bgLinkedBase !== "000") {
      editBase(bgLinkedBase);
    }
  }, [sceneId, bgOption, bgLinkedBase, editBase, initialSceneHasBase, isInitialSceneLoaded]);

  // Stash the base color the user had before switching to generative, so we
  // can put it back when they leave. The broader reset effect above is gated
  // by `sceneId` and never fires for loaded scenes (e.g. Releases pages),
  // which is how the palette base was leaking after exiting generative mode.
  const preGenerativeBaseRef = useRef(null);

  // Set base color when generative background is active
  useEffect(() => {
    if (bgOption !== "generative") {
      if (preGenerativeBaseRef.current !== null) {
        editBase(preGenerativeBaseRef.current);
        preGenerativeBaseRef.current = null;
      }
      return;
    }
    if (preGenerativeBaseRef.current === null) {
      preGenerativeBaseRef.current = base;
    }
    const colors = palettes[product.label];
    if (!colors?.[0]) return;
    editBase(colors[0].replace("#", ""));
    // `base` intentionally omitted: captured only at the not-generative → generative
    // transition; including it would re-snapshot after we overwrite it.
  }, [bgOption, palettes, product.label, editBase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only initialize visible templates when there's no saved scene
    // If sceneId exists, useScene hook will handle loading the saved visible state
    if (!sceneId) {
      const isAllProductsWhatsNew =
        dataChannel === "releases" && product.value === "ALL" && currentSet === "whats-new";
      const productCodes = isAllProductsWhatsNew ? getReleaseProductCodesForType(dataType) : [];
      const allProductsTemplateIds = isAllProductsWhatsNew
        ? getAllProductsTemplateInstanceIds(productCodes, currentSet, [], templateSelection)
        : [];
      const defaultVisibleIds =
        allProductsTemplateIds.length > 0
          ? allProductsTemplateIds
          : drawnTemplatesFor(dataChannel, product.value, templateSelection).map(
              (template) => template.id
            );

      resetVisible();
      defaultVisibleIds.forEach((templateId) => {
        addVisible(templateId);
      });
    }

    editLayoutAmount(layoutAmountFor(dataChannel, product.value));
  }, [dataChannel, product, sceneId, currentSet, dataType, templateSelection]);

  // Reset brightness and blur when switching channels, products, or content types
  // Do NOT reset if a scene is being loaded/restored (to preserve saved values)
  useEffect(() => {
    // Skip on first mount - let defaults or scene values apply
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    // If scene data has been loaded, preserve brightness/blur from the scene
    if (isSceneLoaded) return;

    // Reset to defaults when switching context (channel/product/type)
    editBgBrightness(100);
    editBgBlurValue(0);
  }, [channel.value, product.value, type.value, editBgBrightness, editBgBlurValue, isSceneLoaded]);

  /**
   * Build the scene object from current state — used by save and server export.
   *
   * Scene shape is the union of each context's SCENE_FIELDS export plus
   * `set` (owned by useCurrentSet). Adding a new round-tripped field
   * means adding it to the relevant context's SCENE_FIELDS; this loop
   * picks it up automatically. No hand-maintained list of fields here
   * to drift out of sync with the reducers.
   *
   * Generative animation parameters are unconditionally part of every
   * saved scene (settings exposes them in SCENE_FIELDS). They have safe
   * defaults — null gradients, speed 5000, count 0, stops 3, key 0 —
   * and load is conditional on `data.X !== undefined`, so non-generative
   * scenes round-trip identically.
   */
  const buildScene = useCallback(
    ({forServerExport = false, exportDataRow} = {}) => {
      if (sceneBackgroundSelection.isPending || sceneBackgroundSelection.isError) {
        throw new Error("The selected background has not finished loading.");
      }
      const scene = {set: currentSet};
      for (const f of EDITS_SCENE_FIELDS) scene[f] = editsCtx[f];
      for (const f of IMAGES_SCENE_FIELDS) scene[f] = readImageSceneField(imagesCtx, f);
      for (const f of SETTING_SCENE_FIELDS) scene[f] = settingCtx[f];
      // The saved scene names the offering it belongs to. A product with a
      // home scope draws banners the entry channel does not list, and both
      // the renderer and /scene/:id size a template through the scene's own
      // channel — so the pair must be the home scope, not the way in.
      scene.channel = settingCtx.dataChannelObj;
      scene.type = settingCtx.dataTypeObj;
      // The designs the page draws (data/templateSlots.ts). A saved scene and
      // an export draw them, whatever the live choice is later.
      scene.templateMembers = templateMembersSnapshot(settingCtx.dataChannel, templateSelection);
      // Phase-2 (issue #528, 2a): with bgDerivation on, the bg transform is
      // never written into the images context (imagesCtx.bgX/... are empty), so
      // compose the saved records from the effective-transform selector over the
      // currently-visible templates instead. composeSceneBgTransforms omits ids
      // with no resolved transform, so the saved shape matches the legacy path
      // (untouched templates stay absent). Flag off: the loop above already
      // copied the context records verbatim — this block doesn't run.
      if (bgDerivation) {
        Object.assign(scene, composeSceneBgTransforms(settingCtx.visible, getEffectiveTransform));
      }
      // Pin the image alongside `bgOption` (see libs/sceneBgSource): a
      // promoted upload has no legacy tile folder, and /scene/:id is a
      // legacy-path route.
      if (displayBackgrounds) {
        // `sourceVariation: null` is a VALUE, not a missing field: it is how an
        // upload says it has no catalog image behind it, which is what makes
        // buildSceneBgSource pin it by url instead of by number. Collapsing it
        // onto the position pinned the upload as `{kind: "catalog", variation:
        // <its position>}`, and the loader then looked that position up in a
        // catalog that has no such row — the saved scene opened with no
        // background at all. Only an ABSENT field falls back to the variation.
        const pinEntries = displayBackgrounds.map((b) => ({
          variation: b.variation,
          sourceVariation:
            b.sourceVariation !== undefined ? b.sourceVariation : (b.variation ?? null),
          imageUrl: b.imageUrl,
        }));
        const pinnedUrl = activePinnedBgUrl(settingCtx.pinnedSceneBg, {
          sceneId,
          bgOption: scene.bgOption,
          productValue: settingCtx.product?.value,
        });
        const bgSource = buildSceneBgSource(scene.bgOption, pinEntries, pinnedUrl);
        if (bgSource) scene.bgSource = bgSource;
      }
      // Pixelation knob fields are dropped when the overlay is off, but the
      // gate flag is always persisted: LOAD_SCENE treats absence as "predates
      // the feature", so only an explicit false lets a releases scene
      // round-trip with the overlay off (see prunePixelationForSave).
      prunePixelationForSave(scene);
      if (forServerExport && scene.bgOption === "generative") {
        const frame = settingCtx.getGenerativeFrame();
        if (!frame) {
          throw new Error("Generative background is mounted without a frame snapshot reader");
        }
        scene.generativeExportFrame = frame;
      }
      return exportDataRow === undefined ? scene : materializeVoucherDataRow(scene, exportDataRow);
    },
    [
      editsCtx,
      imagesCtx,
      settingCtx,
      currentSet,
      bgDerivation,
      getEffectiveTransform,
      bgListEditing,
      displayBackgrounds,
      sceneId,
      templateSelection,
      sceneBackgroundSelection.isPending,
      sceneBackgroundSelection.isError,
    ]
  );

  // Expose save-scene for SmartCrop v2 auto-save (avoids prop-drilling through the tree).
  // Returns {scene_id}. Navigation stays enabled unless the caller disables it.
  useEffect(() => {
    window.__saveScene = async ({navigate: shouldNavigate = true} = {}) => {
      if (sceneBackgroundSelection.isPending || sceneBackgroundSelection.isError) {
        return {blocked: "pending-background"};
      }
      // Same blob-URL guard as the React saveScene path; sentinel lets callers bail.
      if (imagesCtx.pendingUploadCount > 0) {
        openErrorDialog({
          title: "Wait for upload to finish",
          summary:
            "One or more images are still uploading. Saving now would leave the scene with a temporary preview that won't load anywhere else. Try again in a moment.",
        });
        return {blocked: "pending-upload"};
      }
      const scene = buildScene();
      const result = await API.saveScene(scene);
      const {scene_id} = result;
      if (scene_id && shouldNavigate) navigate(`/scene/${scene_id}`, {});
      return result;
    };
    return () => {
      delete window.__saveScene;
    };
  });

  // The bake script (scripts/bake-default-backgrounds.ts) reads what each
  // positioned banner on the page would be cut from, the same way a default
  // save does (DefaultScenePanel/bakeOnSave.ts).
  useEffect(() => {
    window.__bgBakeSources = () => collectBakeJobs(registeredBgTemplateIds(), getBgOps);
    return () => {
      delete window.__bgBakeSources;
    };
  }, []);

  // Image export always uses the Playwright renderer. Print CSV rows become
  // request-scoped scenes. Multiple rendered banners use one client-side ZIP
  // and keep the existing row suffix and PDF download contract.
  const exportImages = async () => {
    const startedAt = Date.now();
    const durationMs = () => Date.now() - startedAt;
    editIsExportLoading(true);
    try {
      await sendGAEvent("export", product.value, channel.value);
      const exportRows =
        channel.value === "print" && Array.isArray(dataRows) && dataRows.length > 0
          ? dataRows
          : [undefined];
      const batches = await renderServerExportBatches({
        rows: exportRows,
        buildScene: (row) => buildScene({forServerExport: true, exportDataRow: row}),
        renderScene: renderServerScene,
      });
      await downloadServerExportBundle({
        batches,
        product: product.value,
        // File names come from the template's own record, which lives under
        // the offering's channel: Air's banners reached through Social include
        // ones the Social channel does not list (src/data/offering.ts).
        channel: dataChannel,
        title: titles.main || "",
        set: currentSet,
      });
      track(
        "neurobaker.export.completed",
        buildExportCompletionAnalytics({
          role: authRole,
          kind: "images",
          renderer: "server",
          durationMs: durationMs(),
        })
      );
    } catch (e) {
      const failure = buildExportFailure(e, {
        kind: "images",
        product: product.value,
        channel: channel.value,
        type: type.value,
      });

      console.error("[export] Export failed", failure.diagnostics);
      track(
        "neurobaker.export.failed",
        buildExportFailureAnalytics(e, {
          role: authRole,
          kind: "images",
          renderer: "server",
          durationMs: durationMs(),
        })
      );
      openErrorDialog(failure);
    } finally {
      editIsExportLoading(false);
    }
  };

  const {copyButtonText, copyUrlToClipboard} = useCopyUrl();

  const {mutate, isSuccess, isPending, data} = useMutation({
    mutationKey: ["save-scene"],
    mutationFn: (params) => API.saveScene(params),
    onError: (err, scene) => {
      const introspection = introspectScenePayload(scene);
      const envelope = err.envelope || {};
      const diagnostics = serializeEnvelope(envelope, {
        action: "save-scene",
        scene: {
          sceneId: sceneId || null,
          product: scene?.product ?? null,
          channel: scene?.channel ?? null,
          type: scene?.type ?? null,
          set: scene?.set ?? null,
          layout: scene?.layoutOption ?? null,
          background: scene?.bgOption ?? null,
        },
        inlineBlobsTotalBytes: introspection.totalBytes,
        // Cap at 5 so the diagnostics dump stays paste-friendly when there
        // are many uploads; the totals above still reflect the full set.
        inlineBlobs: introspection.blobs.slice(0, 5),
        inlineBlobsCount: introspection.blobs.length,
      });

      console.error("[save-scene] Save failed", diagnostics);

      const details = [];
      if (envelope.payloadBytes) {
        details.push(`Payload: ${formatBytes(envelope.payloadBytes)}`);
      }
      if (introspection.blobs.length > 0) {
        details.push(
          `Inline images: ${introspection.blobs.length} totalling ${formatBytes(introspection.totalBytes)}`
        );
      }
      if (envelope.elapsedMs !== null && envelope.elapsedMs !== undefined) {
        details.push(`Failed after ${(envelope.elapsedMs / 1000).toFixed(1)}s`);
      }
      if (envelope.requestId) {
        details.push(`Request ID: ${envelope.requestId}`);
      }

      let summary;
      if (err.status === 413) {
        summary =
          "The scene is too large for the server to accept. Try using a smaller background image, or re-upload it at a lower resolution.";
      } else if (envelope.error?.isLikelyCors) {
        summary =
          "The request didn't reach the server — usually means the payload was rejected before a response could be returned. The size of inline images below is often the cause.";
      } else if (err.status) {
        const detail = err.data?.detail ? ` — ${err.data.detail}` : "";
        summary = `Server returned ${err.status} ${envelope.statusText || ""}${detail}`.trim();
      } else {
        summary = "Network request failed. See diagnostics below.";
      }

      openErrorDialog({
        title: "Couldn't save scene",
        summary,
        details,
        diagnostics,
      });
    },
  });

  const saveScene = useCallback(() => {
    // Guard against persisting a `blob:` URL into scene state — those URLs
    // only live in the originating tab and would 404 on every other client
    // (renderer, reload, share). The dropzone flow holds the blob URL in
    // scene state for instant preview, then swaps to the CDN URL once the
    // upload completes; saving before that swap would persist the temp.
    if (imagesCtx.pendingUploadCount > 0) {
      openErrorDialog({
        title: "Wait for upload to finish",
        summary:
          "One or more images are still uploading. Saving now would leave the scene with a temporary preview that won't load anywhere else. Try again in a moment.",
      });
      return;
    }

    // #169 Foundation invariant — shadow-mode for now. Logs + telemetry on
    // any `data:` URI we detect in the scene payload but DOES NOT block the
    // save. Once shadow-mode signals are quiet for a few days, flip the
    // `ENFORCE_NO_INLINE_BLOBS` constant in src/libs/sceneValidation.js to
    // `true` and the same code path will reject with a dialog instead.
    const scene = buildScene();
    const blobCheck = checkSceneForInlineBlobs(scene);
    if (blobCheck.inlineBlobCount > 0) {
      const event = {
        action: "save-scene",
        product: product?.value,
        channel: channel?.value,
        type: type?.value,
        set: currentSet,
        layout: layoutOption,
        background: bgOption,
        sceneId: sceneId || null,
        inlineBlobCount: blobCheck.inlineBlobCount,
        inlineBlobBytes: blobCheck.inlineBlobBytes,
        payloadBytes: blobCheck.payloadBytes,
        topFields: blobCheck.topFields,
        enforceMode: blobCheck.enforceMode,
      };
      console.warn(
        `[save-scene] inline blobs detected (${blobCheck.inlineBlobCount}; ${formatBytes(blobCheck.inlineBlobBytes)})`,
        event
      );
      track("neurobaker.scene_save.inline_blobs_detected", event);

      if (!blobCheck.ok) {
        // Enforce mode: refuse the save and tell the user what to do.
        openErrorDialog({
          title: "Can't save — scene contains inline images",
          summary: `${blobCheck.inlineBlobCount} image(s) in this scene are stored inline instead of as references (legacy format from before the upload-first refactor). Reload the scene and re-upload any custom backgrounds, logos, or template images — they'll go through the new upload path and the save will succeed.`,
          details: [
            `Inline blobs: ${blobCheck.inlineBlobCount} totalling ${formatBytes(blobCheck.inlineBlobBytes)}`,
            `Payload: ${formatBytes(blobCheck.payloadBytes)}`,
            ...blobCheck.topFields.map((f) => `  ${f.path}: ${formatBytes(f.bytes)}`),
          ],
          diagnostics: event,
        });
        return;
      }
    }

    mutate(scene);
  }, [
    mutate,
    buildScene,
    imagesCtx.pendingUploadCount,
    product?.value,
    channel?.value,
    type?.value,
    currentSet,
    layoutOption,
    bgOption,
    sceneId,
  ]);

  // body.image-ready: VR's "page is fully rendered" gate. Set when scene
  // data is in, the bg list query has settled, and every <img> in the
  // bg / upload zones has decoded. Three flavors handled:
  //   - generative-bg / KED scenes: no <img> ever rendered → ready as soon
  //     as the scene + bg-list queries are both settled
  //   - image-bg scenes: ready after decode resolves
  //   - non-scene routes (`/`, `/admin`): ready as soon as bg-list settles
  //     (no async scene fetch to wait for)
  // Pre-#341 this was [bgUrl, uploadImages] only — KED scenes never set
  // the class because pictures.length===0 and the effect never re-fired
  // post-scene-load, and image-bg scenes set it briefly during the gap
  // between scene-loaded and bg-url-resolved.
  useEffect(() => {
    // Readiness observability: record which gate stalls and how long each bg/
    // upload <img> takes to decode, so a `waitForAppReady` timeout can dump the
    // exact cause (see tests/helpers.cjs) instead of leaving us guessing. Kept
    // on window like __renderReady / __getState — array pushes only, no console
    // spam and no behavior change (image-ready still flips after every decode
    // settles).
    const trace = (window.__readinessTrace = window.__readinessTrace || []);
    if (trace.length > 500) trace.splice(0, trace.length - 500);
    const mark = (phase, detail) =>
      trace.push({t: Math.round(performance.now()), phase, ...detail});
    mark("effect:fire", {sceneId, isSceneLoaded, bgPending: !!backgroundsData.isPending});

    if (loadFailure) {
      document.body.classList.remove("image-ready");
      mark("blocked:load-failure", {title: loadFailure.title});
      return;
    }

    if (sceneId && !isSceneLoaded) {
      document.body.classList.remove("image-ready");
      mark("blocked:scene-not-loaded", {sceneId});
      return;
    }
    if (backgroundsData.isPending) {
      document.body.classList.remove("image-ready");
      mark("blocked:bg-list-pending", {});
      return;
    }
    if (savedSceneCatalogPending) {
      document.body.classList.remove("image-ready");
      mark("blocked:saved-scene-catalog-pending", {});
      return;
    }
    // Banners on a baking channel show a loader, not an image, until the
    // default scene is applied (libs/bakedBg.ts). With no <img> yet, the
    // page would read as ready with none of its backgrounds on screen.
    if (awaitingDefaultScene) {
      document.body.classList.remove("image-ready");
      mark("blocked:awaiting-default-scene", {});
      return;
    }
    const pictures = document.querySelectorAll(".bg-transformer img, .image-upload img");
    if (pictures.length === 0) {
      document.body.classList.add("image-ready");
      mark("ready:no-images", {});
      return;
    }
    document.body.classList.remove("image-ready");
    const started = performance.now();
    mark("decode:start", {count: pictures.length});
    Promise.all(
      Array.from(pictures).map((img, i) => {
        const s = performance.now();
        return img.decode().then(
          () => mark("decode:ok", {i, ms: Math.round(performance.now() - s)}),
          (e) =>
            mark("decode:fail", {
              i,
              ms: Math.round(performance.now() - s),
              src: (img.currentSrc || img.src || "").slice(-70),
              err: String((e && e.message) || e).slice(0, 80),
            })
        );
      })
    ).then(() => {
      document.body.classList.add("image-ready");
      mark("ready:decoded", {
        count: pictures.length,
        totalMs: Math.round(performance.now() - started),
      });
    });
  }, [
    bgUrl,
    uploadImages,
    bakedBgs,
    awaitingDefaultScene,
    isSceneLoaded,
    sceneId,
    backgroundsData.isPending,
    savedSceneCatalogPending,
    loadFailure,
  ]);

  useEffect(() => {
    if (isSuccess && !isPending) {
      sendGAEvent("save_scene", product.value, channel.value);
      track("neurobaker.scene.saved");
      const {scene_id} = data;

      navigate(`/scene/${scene_id}`, {});
      // Copy URL to clipboard after navigation
      setTimeout(copyUrlToClipboard, 100);
    }
  }, [data, isSuccess, isPending, copyUrlToClipboard]);

  const renderContent = () => {
    const page = selectBlockingPage({
      sceneNotFound: isError && error?.status === 404,
      wrongBrowser: isWrongBrowserDetected,
      loadFailure,
    });
    if (page === "scene-not-found") return createElement(_SceneNotFound);
    if (page === "wrong-browser") return createElement(_WrongBrowser);
    if (page === "load-error") return createElement(_LoadError, loadFailure);
    return createElement(_Outlet, {context: {sceneData, isSceneLoaded}});
  };

  const isSocialLogoHidden = dataChannel === "social" && logoHidden;
  const isGdnPreview =
    !isRenderMode && isGoogleDisplayNetwork && searchParams.get("preview") === "gdn";
  const closeGdnPreview = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete("preview");
        return params;
      },
      {replace: true}
    );
  }, [setSearchParams]);

  return (
    <BgTransformContext.Provider value={bgTransformContextValue}>
      {isGdnPreview ? (
        <GdnNewsPreview
          onClose={closeGdnPreview}
          creativeClassName={classNames}
          baseColor={`#${base}`}
        />
      ) : (
        <div
          className={`App${classNames}${isRenderMode ? " render-mode" : ""}${
            isSocialLogoHidden ? " logo-hidden-social" : ""
          }`}
        >
          <main>
            {pointCloudError && <p role="alert">{pointCloudError}</p>}
            <div className="templates">
              <div
                className="templates-inner"
                style={{
                  "--base-color": `#${base}`,
                }}
              >
                {renderContent()}
              </div>
            </div>
            {!isRenderMode &&
              !loadFailure &&
              createElement(_Toolbar, {
                handleExport: exportImages,
                handleSaveScene: saveScene,
                buildScene,
                copyButtonText,
                isWrongBrowser: isWrongBrowserDetected,
                backgroundsData,
                bgDisplayOrder,
                linkGallery,
                isLinkGalleryPending,
                linkBackground,
                isChannelReady,
                isRouteDefaultReady: isInitialSceneReady,
              })}
          </main>
        </div>
      )}
    </BgTransformContext.Provider>
  );
};

export default App;
